import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import * as yup from 'yup';
import { useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useFormWithSchema } from '../../../utils/functions/yupFormTypescript';
import {
  AutoCompleteController,
  InputController,
} from '../../../components';
import {
  CascadeField,
  useCropSeasonOptions,
} from '../hooks/useCropSeasonOptions';
import { useProductOptions } from '../hooks/useProductOptions';
import {
  buildLineSchema,
  StageRanges,
} from '../utils/scheduleLineSchema';
import { useStageOptions } from '../hooks/useStageOptions';
import { useScheduleLineAutofill } from '../hooks/useScheduleLineAutofill';
import LockedField from './lockedField';
import {
  DownloadTemplateLink,
  ImportedLine,
  ScheduleFileUpload,
  useScheduleFileImport,
} from './scheduleFileUpload';
import arrow from '../../../assets/icons/line_arrow-close-left.svg';

const TEAL = 'rgba(0,105,112,1)';

export type TScheduleLine = {
  itemId?: number;
  draft?: boolean;
  product: string;
  productLabel?: string;
  uplProduct?: boolean;
  stage: string;
  dasFrom: string;
  dasTo: string;
  mandatory: string;
};

export type TScheduleForm = {
  country: string;
  region: string;
  season: string;
  crop: string;
  hybridProductionCode: string;
  remarks?: string;
  status?: string;
  hasPendingEdit?: boolean;
  stage: string;
  product: string;
  dasFrom: string;
  dasTo: string;
  mandatory: string;
  lines?: TScheduleLine[];
};

const emptyLine: TScheduleLine = {
  product: '',
  stage: '',
  dasFrom: '',
  dasTo: '',
  mandatory: '',
};

const defaultValues: TScheduleForm = {
  country: '',
  region: '',
  season: '',
  crop: '',
  hybridProductionCode: '',
  remarks: '',
  stage: '',
  product: '',
  dasFrom: '',
  dasTo: '',
  mandatory: '',
  lines: [],
};


const scheduleSchema = yup.object({  
  country: yup.string().nullable(),
  region: yup.string().nullable(),
  season: yup.string().required('Season is required'),
  crop: yup.string().required('Crop is required'),
  hybridProductionCode: yup
    .string()
    .required('Hybrid Production Code is required'),
  remarks: yup.string().nullable(),
  stage: yup.string().when('lines', {
    is: (lines: any[]) => !lines || lines.length === 0,
    then: (s) => s.required('Stage is required'),
    otherwise: (s) => s.optional(),
  }),
  product: yup.string().when('lines', {
    is: (lines: any[]) => !lines || lines.length === 0,
    then: (s) => s.required('Product is required'),
    otherwise: (s) => s.optional(),
  }),
  dasFrom: yup.string().when('lines', {
    is: (lines: any[]) => !lines || lines.length === 0,
    then: (s) => s.required('DAS From is required'),
    otherwise: (s) => s.optional(),
  }),
  dasTo: yup.string().when('lines', {
    is: (lines: any[]) => !lines || lines.length === 0,
    then: (s) => s.required('DAS To is required'),
    otherwise: (s) => s.optional(),
  }),
  mandatory: yup.string().when('lines', {
    is: (lines: any[]) => !lines || lines.length === 0,
    then: (s) => s.required('Mandatory is required'),
    otherwise: (s) => s.optional(),
  }),
  lines: yup.array().of(buildLineSchema(false)),
});

const COUNTRY_OPTIONS = [
  { value: 'INDIA', label: 'India' },
  { value: 'THAILAND', label: 'Thailand' },
];

const STAGE_OPTIONS = [
  { value: 'Land Preparation', label: 'Land Preparation' },
  { value: 'VEGETATIVE', label: 'Vegetative' },
  { value: 'FLOWERING', label: 'Flowering' },
  { value: 'Pre Harvest', label: 'Pre Harvest' },
  { value: 'HARVEST', label: 'Harvest' },
];



const MANDATORY_OPTIONS = [
  { value: 'YES', label: 'YES' },
  { value: 'NO', label: 'NO' },
];

type ScheduleDrawerProps = {
  open: boolean;
  onClose: () => void;
  mode?: 'add' | 'duplicate' | 'edit';
  initialData?: Partial<TScheduleForm> | null;
  onSave?: (
    data: TScheduleForm
  ) => Promise<boolean | void> | boolean | void;
  onPublish?: (
    data: TScheduleForm
  ) => Promise<boolean | void> | boolean | void;
  onDelete?: () => Promise<boolean | void> | boolean | void;
  saving?: boolean;
};

export default function ScheduleDrawer({
  open,
  onClose,
  mode = 'add',
  initialData,
  onSave,
  onPublish,
  onDelete,
  saving = false,
}: ScheduleDrawerProps) {
  const countryName = useSelector(
    (state: RootState) => state.country.countryName
  );
  
  const lockedCountry =
    initialData?.country ||
    COUNTRY_OPTIONS.find(
      (opt) =>
        opt.value.toLowerCase() ===
        (countryName || '').toLowerCase()
    )?.value ||
    '';

  const validationContext = React.useRef<{
    stageRanges: StageRanges;
  }>({ stageRanges: {} }).current;
  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useFormWithSchema(scheduleSchema, {
    mode: 'onChange',
    defaultValues,
    context: validationContext,
  });

  const { fields, prepend, remove, replace } =
    useFieldArray({
      control,
      name: 'lines',
    });
  
  const originalLineIdsRef = React.useRef<Set<string>>(
    new Set()
  );
  
  const lockedProductByIdRef = React.useRef<
    Record<string, string>
  >({});
  const capturedRef = React.useRef(false);
  const initialLinesRef = React.useRef<string>('[]');


  const isEdit = mode === 'edit';
  const multiLine = mode === 'edit' || mode === 'duplicate';

  const [watchRegion, watchSeason, watchCrop, watchHybrid] =
    watch([
      'region',
      'season',
      'crop',
      'hybridProductionCode',
    ]);

  const clearField = useCallback(
    (field: CascadeField) => {
      const opts = { shouldValidate: true };
      if (field === 'season') setValue('season', '', opts);
      else if (field === 'region') setValue('region', '', opts);
      else if (field === 'crop') setValue('crop', '', opts);
      else if (field === 'hybrid')
        setValue('hybridProductionCode', '', opts);
    },
    [setValue]
  );

  
  const {
    seasonOptions,
    regionOptions,
    cropOptions,
    hybridOptions,
  } = useCropSeasonOptions({
    enabled: open && !isEdit,
    season: watchSeason,
    region: watchRegion ?? undefined,
    crop: watchCrop,
    hybrid: watchHybrid,
    clear: clearField,
    lockedValues: isEdit
      ? {
          season: initialData?.season,
          region: initialData?.region,
          crop: initialData?.crop,
          hybrid: initialData?.hybridProductionCode,
        }
      : undefined,
  });
  
  const duplicateHeaderModified =
    mode === 'duplicate'
      ? (watchSeason ?? '') !==
          (initialData?.season ?? '') ||
        (watchCrop ?? '') !== (initialData?.crop ?? '') ||
        (watchHybrid ?? '') !==
          (initialData?.hybridProductionCode ?? '')
      : true;

  
  const watchLines = watch('lines');
  const linesChanged =
    JSON.stringify(
      (watchLines || []).map((l: any) => ({
        stage: l?.stage ?? '',
        dasFrom: l?.dasFrom ?? '',
        dasTo: l?.dasTo ?? '',
        mandatory: l?.mandatory ?? '',
      }))
    ) !== initialLinesRef.current;

  
  const hasRealChange = isEdit ? linesChanged : true;
  const isPendingSchedule =
    isEdit && initialData?.status === 'Pending';
  const hasPendingEdit = isEdit && !!initialData?.hasPendingEdit;
  const serverDelete = isPendingSchedule || hasPendingEdit;
  const showDelete =
    multiLine &&
    !(mode === 'duplicate' && initialData?.status === 'Inactive');

  
  const canSave =
    (isEdit
      ? hasRealChange || serverDelete
      : Boolean(
          lockedCountry?.trim() &&
            watchSeason?.trim() &&
            watchCrop?.trim() &&
            watchHybrid?.trim()
        )) &&
    duplicateHeaderModified;

  
  const {
    productOptions,
    uplByProductId: apiUplByProductId,
    loaded: productsLoaded,
  } = useProductOptions(
      open,
      isEdit
        ? {
            region: initialData?.region,
            crop: initialData?.crop,
            hybrid: initialData?.hybridProductionCode,
          }
        : {
            region: watchRegion,
            crop: watchCrop,
            hybrid: watchHybrid,
          }
    );
  const productFilterKey = [
    watchRegion ?? '',
    watchCrop ?? '',
    watchHybrid ?? '',
  ].join('|');
  const duplicateFilterChanged =
    mode === 'duplicate' &&
    productFilterKey !==
      [
        initialData?.region ?? '',
        initialData?.crop ?? '',
        initialData?.hybridProductionCode ?? '',
      ].join('|');

  const [importedLines, setImportedLines] = useState<
    ImportedLine[]
  >([]);

  const lineProductOptions = useMemo(() => {
    const known = new Set(productOptions.map((o) => o.value));
    const extra = new Map<string, string>();
    [
      ...(duplicateFilterChanged ? [] : initialData?.lines ?? []),
      ...importedLines,
    ].forEach((l) => {
      if (l.product && !known.has(l.product)) {
        extra.set(l.product, l.productLabel || l.product);
      }
    });
    return [
      ...productOptions,
      ...Array.from(extra, ([value, label]) => ({
        value,
        label,
      })),
    ];
  }, [
    productOptions,
    initialData,
    duplicateFilterChanged,
    importedLines,
  ]);

  const filteredKeyRef = React.useRef('');
  useEffect(() => {
    if (!open) {
      filteredKeyRef.current = '';
      return;
    }
    if (
      !duplicateFilterChanged ||
      !productsLoaded ||
      filteredKeyRef.current === productFilterKey
    ) {
      return;
    }
    filteredKeyRef.current = productFilterKey;
    const available = new Set(productOptions.map((o) => o.value));
    const current = getValues('lines') ?? [];
    const kept = current.filter(
      (line) => !line?.product || available.has(line.product)
    );
    if (kept.length !== current.length) {
      replace(kept as TScheduleLine[]);
    }
  }, [
    open,
    duplicateFilterChanged,
    productsLoaded,
    productFilterKey,
    productOptions,
    getValues,
    replace,
  ]);
  const uplByProductId = useMemo(() => {
    const map: Record<string, boolean> = {};
    [...(initialData?.lines ?? []), ...importedLines].forEach((l) => {
      if (l.product) map[l.product] = l.uplProduct === true;
    });
    return { ...map, ...apiUplByProductId };
  }, [initialData, importedLines, apiUplByProductId]);

  const {
    stageOptions,
    stageRanges,
    requestStages,
    loading: stagesLoading,
  } = useStageOptions({
    enabled: open,
    crop: isEdit ? initialData?.crop : watchCrop,
    hybrid: isEdit
      ? initialData?.hybridProductionCode
      : watchHybrid,
    season: isEdit ? initialData?.season : watchSeason,
  });
  validationContext.stageRanges = stageRanges;

  useEffect(() => {
    if (open && fields.length > 0) requestStages();
  }, [open, fields.length, requestStages]);

  useEffect(() => {
    if (open && fields.length > 0) trigger('lines');
  }, [open, stageRanges, fields.length, trigger]);

  const fileImport = useScheduleFileImport({
    header: isEdit
      ? {
          season: initialData?.season,
          region: initialData?.region,
          crop: initialData?.crop,
          hybrid: initialData?.hybridProductionCode,
        }
      : {
          season: watchSeason,
          region: watchRegion,
          crop: watchCrop,
          hybrid: watchHybrid,
        },
    onItems: (lines) => {
      setImportedLines(lines);
      replace(lines);
      requestStages();
    },
    onClear: () => {
      setImportedLines([]);
      replace([]);
    },
  });
  const { resetFile } = fileImport;
  const showFileUpload =
    multiLine && (fields.length === 0 || !!fileImport.file);

  useEffect(() => {
    if (open) {
      resetFile();
      setImportedLines([]);
    }
  }, [open, resetFile]);

  const lineStageOptions = useMemo(() => {
    const known = new Set(stageOptions.map((o) => o.value));
    const extra = Array.from(
      new Set(
        [
          ...(initialData?.lines ?? []).map((l) => l.stage),
          ...(watchLines ?? []).map((l) => l?.stage),
        ].filter((v): v is string => !!v && !known.has(v))
      )
    ).map((value) => ({ value, label: value }));
    return [...stageOptions, ...extra];
  }, [stageOptions, initialData, watchLines]);

  const lineProduct = (index: number) =>
    watchLines?.[index]?.product ||
    lockedProductByIdRef.current[fields[index]?.id] ||
    '';

  useScheduleLineAutofill({
    lines: (watchLines ?? []).map((line, index) => ({
      ...line,
      product: lineProduct(index),
    })),
    rowIds: fields.map((field) => field.id),
    setValue,
    uplByProductId,
    stageRanges,
  });

  const rangeOf = (index: number) =>
    stageRanges[watchLines?.[index]?.stage ?? ''];

  const isUplLine = (index: number) =>
    uplByProductId[lineProduct(index)] === true;

  useEffect(() => {
    if (open) {
      const prefill =
        (mode === 'duplicate' || mode === 'edit') &&
        initialData
          ? initialData
          : {};

    
      const lines =
        mode === 'edit' || mode === 'duplicate'
          ? prefill?.lines ?? []
          : [];

      
      capturedRef.current = lines.length === 0;
      originalLineIdsRef.current = new Set();
      lockedProductByIdRef.current = {};
      
      initialLinesRef.current = JSON.stringify(
        (lines as any[]).map((l) => ({
          stage: l.stage ?? '',
          dasFrom: l.dasFrom ?? '',
          dasTo: l.dasTo ?? '',
          mandatory: l.mandatory ?? '',
        }))
      );

      reset({
        ...defaultValues,
        ...prefill,
        country: lockedCountry,
        lines,
      });
    }
  }, [open, mode, initialData, reset, lockedCountry]);

  
  useEffect(() => {
    if (open && !capturedRef.current && fields.length > 0) {
      originalLineIdsRef.current = new Set(
        fields.map((f) => f.id)
      );
      lockedProductByIdRef.current = fields.reduce(
        (acc, f: any) => {
          acc[f.id] = f.product;
          return acc;
        },
        {} as Record<string, string>
      );
      capturedRef.current = true;
    }
  }, [open, fields]);

  const handleClose = () => {
    reset({ ...defaultValues, country: lockedCountry });
    onClose();
  };

  
  const buildPayload = (): TScheduleForm => {
    const values = getValues() as TScheduleForm;
    return {
      ...values,
      country: lockedCountry,
      lines: (values.lines || []).map((line, i) => {
        const fieldId = fields[i]?.id;
        const lockedProduct = fieldId
          ? lockedProductByIdRef.current[fieldId]
          : undefined;
        return {
          ...line,
          itemId:
            mode === 'edit'
              ? line.itemId ?? (fields[i] as TScheduleLine)?.itemId
              : undefined,
          draft:
            mode === 'edit'
              ? line.draft ?? (fields[i] as TScheduleLine)?.draft
              : undefined,
          product: line.product || lockedProduct || '',
        };
      }),
    };
  };


  const handleSave = async () => {
    if (!canSave || stagesLoading) return;
    if (fields.length > 0 && !(await trigger('lines'))) return;
    const result = await onSave?.(buildPayload());
    if (result !== false) handleClose();
  };

  const [publishConfirmOpen, setPublishConfirmOpen] =
    useState(false);
  const requestPublish = handleSubmit(() => {
    setPublishConfirmOpen(true);
  });

  const confirmPublish = async () => {
    setPublishConfirmOpen(false);
    const result = await onPublish?.(buildPayload());
    if (result !== false) handleClose();
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] =
    useState(false);

  
  const confirmDelete = async () => {
    setDeleteConfirmOpen(false);
    if (serverDelete) {
      const result = await onDelete?.();
      if (result !== false) handleClose();
      return;
    }
    replace([]);
    onDelete?.();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '50vw',
          backgroundColor: '#fff',
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
        }}>
        <Typography
          fontSize={17}
          fontWeight={600}
          sx={{ lineHeight: 1 }}>
          {mode === 'edit'
            ? 'Edit Schedule'
            : mode === 'duplicate'
            ? 'Duplicate Schedule'
            : 'Add Schedule'}
        </Typography>

        <Box
          onClick={handleClose}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <img
            src={arrow}
            alt="close"
            style={{ width: 20, height: 20 }}
          />
        </Box>
      </Box>
      <Box
        component="form"
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          pt: 1,
          pb: 10,
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
            },
            columnGap: 2,
            rowGap: 0,
            '& .form-control_autocomplete.h-28': {
              height: 'auto',
            },
            '& .form-field-label': {
              marginTop: '6px',
            },
            '& .input-field input': {
              marginBottom: 0,
            },
            '& .form-field-error': {
              margin: 0,
              minHeight: '16px',
              lineHeight: '16px',
              fontSize: '11px',
            },
          }}>
          <AutoCompleteController
            control={control}
            name="country"
            label="Country"
            placeholder="Select Country"
            options={COUNTRY_OPTIONS}
            required
            error={errors?.country?.message}
            disableClearable
            locked
          />
          <AutoCompleteController
            control={control}
            name="region"
            label="Region"
            placeholder="Select Region"
            options={regionOptions}
            required
            error={errors?.region?.message}
            disableClearable
            locked={isEdit}
          />
          <AutoCompleteController
            control={control}
            name="season"
            label="Season"
            placeholder="Select Season"
            options={seasonOptions}
            required
            error={errors?.season?.message}
            disableClearable
            locked={isEdit}
          />
          <AutoCompleteController
            control={control}
            name="crop"
            label="Crop"
            placeholder="Select Crop"
            options={cropOptions}
            required
            error={errors?.crop?.message}
            disableClearable
            locked={isEdit}
          />
          <AutoCompleteController
            control={control}
            name="hybridProductionCode"
            label="Hybrid Production Code"
            placeholder="Select Code"
            options={hybridOptions}
            required
            error={errors?.hybridProductionCode?.message}
            disableClearable
            locked={isEdit}
          />
          {!isEdit && (
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <InputController
                label="Remarks"
                placeholder="Enter remarks"
                register={register('remarks')}
                error={errors?.remarks?.message}
              />
            </Box>
          )}

          {mode === 'add' && (
            <>
              <AutoCompleteController
                control={control}
                name="stage"
                label="Stage"
                placeholder="Select stage"
                options={STAGE_OPTIONS}
                required
                error={errors?.stage?.message}
                disableClearable
              />
              <AutoCompleteController
                control={control}
                name="product"
                label="Product"
                placeholder="Select product"
                options={lineProductOptions}
                required
                error={errors?.product?.message}
                disableClearable
              />

              <Box>
                <InputController
                  label="DAS From"
                  placeholder="Enter value"
                  register={register('dasFrom')}
                  error={errors?.dasFrom?.message}
                  required
                />
                {!errors?.dasFrom?.message && (
                  <Typography
                    fontSize={11}
                    color="#8a8a8a"
                    sx={{ mt: '-14px' }}>
                    Negative Values are allowed
                  </Typography>
                )}
              </Box>
              <Box>
                <InputController
                  label="DAS To"
                  placeholder="Enter value"
                  register={register('dasTo')}
                  error={errors?.dasTo?.message}
                  required
                />
                {!errors?.dasTo?.message && (
                  <Typography
                    fontSize={11}
                    color="#8a8a8a"
                    sx={{ mt: '-14px' }}>
                    Must be greater than DAS From
                  </Typography>
                )}
              </Box>

              <AutoCompleteController
                control={control}
                name="mandatory"
                label="Mandatory"
                placeholder="Select"
                options={MANDATORY_OPTIONS}
                required
                error={errors?.mandatory?.message}
                disableClearable
              />
            </>
          )}
        </Box>

        {multiLine && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1.5,
                mb: 1,
              }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#444">
                Schedule Line ({fields.length})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}>
                {showFileUpload && (
                  <>
                    <DownloadTemplateLink {...fileImport} />
                    <Box
                      sx={{
                        width: '1px',
                        height: 16,
                        backgroundColor: '#d0d5dd',
                      }}
                    />
                  </>
                )}
                <Link
                  component="button"
                  type="button"
                  underline="none"
                  onClick={() => {
                    requestStages();
                    prepend(emptyLine);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    fontSize: 14,
                    color: TEAL,
                    fontWeight: 500,
                  }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                  Add
                </Link>
              </Box>
            </Box>

            {showFileUpload && <ScheduleFileUpload {...fileImport} />}

            {fields.map((lineField, index) => (
              <Box
                key={lineField.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  width: 688,
                  maxWidth: '100%',
                  minHeight: 73,
                  boxSizing: 'border-box',
                  border: '1px solid #EFEFEF',
                  backgroundColor: '#F2F2F2',
                  borderRadius: '4px',
                  p: '8px',
                  mb: 1.5,
                  
                  '& .form-control_autocomplete.h-28': {
                    height: 'auto',
                  },
                  '& .form-field': {
                    marginBottom: 0,
                  },
                  
                  '& .form-field-error': {
                    display: 'none',
                  },
                  '& .form-field-label': {
                    marginTop: 0,
                    marginBottom: '4px',
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#667085',
                  },
                  
                  '& .input-field input': {
                    boxSizing: 'border-box',
                    margin: '0 !important',
                    height: '36px !important',
                    fontSize: '13px',
                    borderRadius: '4px',
                    border: '0.5px solid #d0d5dd',
                    backgroundColor: '#fff',
                  },
                  
                  '& .custom-autocomplete .MuiAutocomplete-inputRoot':
                    {
                      boxSizing: 'border-box',
                      height: '36px !important',
                      minHeight: '36px !important',
                      padding: '0 8px !important',
                    },
                  '& .custom-autocomplete .MuiInputBase-root':
                    {
                      boxSizing: 'border-box',
                      height: '36px !important',
                      minHeight: '36px !important',
                      fontSize: '13px',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                    },
                  '& .custom-autocomplete input': {
                    fontSize: '13px',
                    padding: '0 !important',
                  },
                  '& .custom-autocomplete fieldset': {
                    borderWidth: '0.5px',
                  },
                  
                  '& .input-field input:disabled': {
                    backgroundColor: '#eceef1',
                    color: '#98a2b3',
                    cursor: 'not-allowed',
                  },
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#eceef1',
                  },
                }}>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AutoCompleteController
                    control={control}
                    name={`lines.${index}.product`}
                    label="Product"
                    placeholder="Select product"
                    options={lineProductOptions}
                    disableClearable
                    locked={
                      isEdit &&
                      originalLineIdsRef.current.has(
                        lineField.id
                      )
                    }
                    height={36}
                    error={
                      errors?.lines?.[index]?.product
                        ?.message
                    }
                  />
                </Box>
                <Box
                  sx={{
                    width: 152,
                    flexShrink: 0,
                  }}>
                  <AutoCompleteController
                    control={control}
                    name={`lines.${index}.stage`}
                    label="Stage"
                    placeholder="Select stage"
                    options={lineStageOptions}
                    disableClearable
                    height={36}
                    error={
                      errors?.lines?.[index]?.stage
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  <InputController
                    label="DAS From"
                    placeholder={
                      rangeOf(index)?.dasFrom != null
                        ? `≥ ${rangeOf(index)?.dasFrom}`
                        : '0'
                    }
                    title={
                      errors?.lines?.[index]?.dasFrom?.message
                    }
                    register={register(
                      `lines.${index}.dasFrom`
                    )}
                    error={
                      errors?.lines?.[index]?.dasFrom
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  <InputController
                    label="DAS To"
                    placeholder={
                      rangeOf(index)?.dasTo != null
                        ? `≤ ${rangeOf(index)?.dasTo}`
                        : '0'
                    }
                    title={
                      errors?.lines?.[index]?.dasTo?.message
                    }
                    register={register(
                      `lines.${index}.dasTo`
                    )}
                    error={
                      errors?.lines?.[index]?.dasTo
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  {isUplLine(index) ? (
                    <LockedField label="Mandatory" value="YES" />
                  ) : (
                    <AutoCompleteController
                      control={control}
                      name={`lines.${index}.mandatory`}
                      label="Mandatory"
                      placeholder="Select"
                      options={MANDATORY_OPTIONS}
                      disableClearable
                      height={36}
                      error={
                        errors?.lines?.[index]?.mandatory
                          ?.message
                      }
                    />
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => remove(index)}
                  sx={{ mt: 3, flexShrink: 0 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </>
        )}
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          display: 'flex',
          justifyContent: showDelete
            ? 'space-between'
            : 'flex-end',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          borderTop: '1px solid #E0E0E0',
          backgroundColor: '#fff',
        }}>
        {showDelete && (
          <Button
            variant="outlined"
            disabled={
              serverDelete
                ? saving
                : fields.length === 0 ||
                  (isEdit ? !hasRealChange : false)
            }
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              color: TEAL,
              borderColor: TEAL,
              fontWeight: 500,
              '&.Mui-disabled': {
                color: 'rgba(0,105,112,0.4)',
                borderColor: 'rgba(0,105,112,0.3)',
              },
            }}>
            Delete
          </Button>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <Button
            variant="outlined"
            disabled={
              !canSave ||
              saving ||
              stagesLoading ||
              fileImport.uploading
            }
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              color: TEAL,
              borderColor: TEAL,
              fontWeight: 500,
              '&.Mui-disabled': {
                color: 'rgba(0,105,112,0.4)',
                borderColor: 'rgba(0,105,112,0.3)',
              },
            }}>
            Save
          </Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              fileImport.uploading ||
              !isValid ||
              (!isEdit && !watchRegion) ||
              !duplicateHeaderModified ||
              (isEdit && !hasRealChange && !serverDelete)
            }
            onClick={requestPublish}
            sx={{
              textTransform: 'none',
              backgroundColor: TEAL,
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0,105,112,0.4)',
                color: '#fff',
              },
            }}>
            Publish Schedule
          </Button>
        </Box>
      </Box>

      <Dialog
        open={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        fullWidth
        maxWidth="xs">
        <DialogTitle
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: '#4D4D4D',
          }}>
          Publish Schedule
          <IconButton
            aria-label="close"
            onClick={() => setPublishConfirmOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#4D4D4D',
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="#4d4d4d">
            Are you sure you want to publish this schedule?
            Once published, it will be available for
            application.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setPublishConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              color: TEAL,
              borderColor: TEAL,
              fontWeight: 500,
              '&:hover': {
                borderColor: 'rgba(0,105,112,0.8)',
                backgroundColor: 'rgba(0,105,112,0.05)',
              },
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPublish}
            sx={{
              textTransform: 'none',
              backgroundColor: TEAL,
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
            }}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="xs">
        <DialogTitle
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: '#4D4D4D',
          }}>
          Delete Schedule
          <IconButton
            aria-label="close"
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#4D4D4D',
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="#4d4d4d">
            {hasPendingEdit
              ? 'Are you sure you want to delete the pending edits? The published schedule will stay as it is.'
              : 'Are you sure you want to delete this schedule? Once deleted, it will be removed permanently.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              color: TEAL,
              borderColor: TEAL,
              fontWeight: 500,
              '&:hover': {
                borderColor: 'rgba(0,105,112,0.8)',
                backgroundColor: 'rgba(0,105,112,0.05)',
              },
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            sx={{
              textTransform: 'none',
              backgroundColor: TEAL,
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
